/* eslint-disable arrow-body-style */
/* eslint-disable no-else-return */
/* eslint-disable no-use-before-define */
import _ from 'underscore';
import { useCallback, useMemo, useContext } from 'react';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { Constants, PaymentUtils } from '@bingads-webui-clientcenter/entity-utils';
import { usePaymentInstrumentsFetch, useCountriesFetch, useCurrenciesFetch } from '../use-entity';
import usePaymentInfoI18nContext from './use-i18n-context';
import useErrorLogs from './use-error-logs';

export default function useDataRestrictions(scope, restrictions) {
  const dataRestrictions = [
    usePaymentTypeOptions(restrictions.paymentTypes),
    useCountryOptions(scope, restrictions.currency, restrictions.countries),
    useSupportedPiTypes(restrictions.paymentMethods),
    usePiOptions(scope),
  ];

  return {
    ..._.reduce(dataRestrictions, (memo, r) => ({ ...memo, ...r }), {}),
    isLoading: _.some(dataRestrictions, r => r.isLoading),
  };
}

const PaymentTypes = [Constants.PaymentTypeId.Prepay, Constants.PaymentTypeId.Postpay];
function usePaymentTypeOptions(paymentTypeFilter) {
  const paymentTypes = useFilter(PaymentTypes, paymentTypeFilter);

  return {
    paymentTypeOptions: paymentTypes,
  };
}

function useCountryOptions(scope, currencyCode, countryFilter) {
  // TODO: skip loading data if countryFilter already contains the list of supported countries
  const { data: allCountries, error: countriesError } = useCountriesFetch(scope);
  const { data: allCurrencies, error: currenciesError } = useCurrenciesFetch(scope);
  // TODO: support error handling
  const compatibleCountries = useCallback(() => {
    if (allCountries == null) {
      return [];
    } else if (allCurrencies == null) {
      const allCountryValues = _.pluck(allCountries, 'value');
      return _.filter(allCountryValues, country => country.Currency === currencyCode);
    } else {
      const controlledCurrencyCountryMap = _.chain(allCurrencies).pluck('value').reduce((map, currency) => {
        if (currency.ControlledCurrencyCountryId != null) {
          // eslint-disable-next-line no-param-reassign
          map[currency.Code.toUpperCase()] = currency.ControlledCurrencyCountryId;
        }

        return map;
      }, {}).value();

      const allCountryValues = _.pluck(allCountries, 'value');
      const controlledCurrencyCountryId = currencyCode != null && controlledCurrencyCountryMap[currencyCode];

      return controlledCurrencyCountryId != null
        ? _.filter(allCountryValues, country => country.Id === controlledCurrencyCountryId)
        : _.filter(allCountryValues, country => !_.contains(_.values(controlledCurrencyCountryMap), country.Id));
    }
  }, [allCountries, allCurrencies, currencyCode]);

  useErrorLogs([countriesError, 'Country load failed: '], [currenciesError, 'Currency load failed: ']);

  const countryOptions = useFilter(compatibleCountries, countryFilter);

  return {
    countryOptions,
    isLoading: !currencyCode || (!allCountries && !countriesError) || (!allCurrencies && !currenciesError),
  };
}

// if we want to support payment method filtering, e.g. exclude "PayPal" in some cases, we need to override the PaymentContext
function useSupportedPiTypes(paymentMethodFilter) {
  const originalPaymentContext = useContext(PaymentContext);
  const supportedPiTypes = useFilter(originalPaymentContext.paymentContext.SupportedPiTypes, paymentMethodFilter);
  const paymentContext = useMemo(() => ({
    ...originalPaymentContext,
    paymentContext: {
      ...originalPaymentContext.paymentContext,
      SupportedPiTypes: supportedPiTypes,
    },
  }), [originalPaymentContext, supportedPiTypes]);

  return {
    paymentContext,
  };
}

const EmptyArray = Object.freeze([]);

function usePiOptions(scope) {
  const { data: paymentInstruments, error: piError } = usePaymentInstrumentsFetch(scope);
  const { legacyI18n } = usePaymentInfoI18nContext();

  const decoratedPaymentInstruments = useMemo(() => {
    // NOTE: "sortPiList" does not simply sort, it also groups and processes the items, enhancing them with more data; so it's a terribly misleading name
    return _.isEmpty(paymentInstruments)
      ? EmptyArray
      : PaymentUtils.sortPiList(paymentInstruments, legacyI18n);
  }, [paymentInstruments, legacyI18n]);

  useErrorLogs([piError, 'PI load failed: ']);

  // NOTE: if we fail to load existing PIs, we move on as if no PIs exist
  return {
    piOptions: decoratedPaymentInstruments || (piError && EmptyArray),
    isLoading: !paymentInstruments && !piError,
  };
}

function useFilter(dataSource, filter) {
  // TODO: maybe support promises?
  const filteredCollection = useMemo(() => {
    const collection = _.isFunction(dataSource) ? dataSource() : dataSource;
    if (!filter) {
      return collection;
    } else if (_.isFunction(filter)) {
      return filter(collection);
    } else {
      return filter;
    }
  }, [dataSource, filter]);

  return filteredCollection;
}
